import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { db } from "../firebaseConfig";

function isActiveDoc(x) {
  return !(x?.isArchived === true || x?.archived === true || x?.disabled === true);
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth() {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toJsDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function money(n) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function pct(n) {
  return `${Number(n || 0).toFixed(1)}%`;
}

function formatHours(n) {
  const num = Number(n || 0);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return `${num.toFixed(1)} hrs`;
}

function formatDateTime(value) {
  const dt = toJsDate(value);
  if (!dt) return "—";
  return new Intl.DateTimeFormat("en-ZA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
}

function shortDate(value) {
  const dt = toJsDate(value);
  if (!dt) return "—";
  return new Intl.DateTimeFormat("en-ZA", {
    month: "short",
    day: "numeric",
  }).format(dt);
}

function getStatusTone(status) {
  const key = String(status || "").toLowerCase();
  if (key === "open") return "bg-blue-500/20 text-blue-200 border-blue-400/20";
  if (key === "in progress") return "bg-cyan-500/20 text-cyan-200 border-cyan-400/20";
  if (key === "waiting on client" || key === "waiting") return "bg-amber-500/20 text-amber-200 border-amber-400/20";
  if (key === "resolved") return "bg-emerald-500/20 text-emerald-200 border-emerald-400/20";
  if (key === "closed") return "bg-slate-500/20 text-slate-200 border-slate-400/20";
  return "bg-white/10 text-white/70 border-white/10";
}

function StatCard({ label, value, sub, accent = "text-cyan-300", tone = "bg-white/5" }) {
  return (
    <div className={`rounded-2xl border border-white/10 ${tone} p-5`}>
      <div className="text-sm text-white/60">{label}</div>
      <div className={`mt-2 text-3xl font-bold ${accent}`}>{value}</div>
      {sub ? <div className="mt-2 text-xs text-white/50">{sub}</div> : null}
    </div>
  );
}

function SectionTitle({ title, hint }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      {hint ? <p className="mt-1 text-sm text-white/60">{hint}</p> : null}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3 last:border-b-0 last:pb-0">
      <div className="text-white/60">{label}</div>
      <div className="font-semibold text-white">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${getStatusTone(status)}`}>
      {status || "Unknown"}
    </span>
  );
}

function Stars({ value }) {
  const n = Math.max(0, Math.min(5, Number(value || 0)));
  return (
    <div className="text-amber-300 tracking-[0.18em]">
      {"★".repeat(Math.floor(n))}
      <span className="text-white/15">{"★".repeat(5 - Math.floor(n))}</span>
    </div>
  );
}

function MiniMetric({ label, value, tone }) {
  return (
    <div className={`rounded-xl border border-white/10 p-4 ${tone}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-white/60">{label}</div>
      <div className="mt-2 text-3xl font-bold text-white">{value}</div>
    </div>
  );
}

function ChartCard({ title, hint, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <SectionTitle title={title} hint={hint} />
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-sm text-white/50">{text}</div>;
}

const chartTheme = {
  axis: "rgba(255,255,255,0.58)",
  grid: "rgba(255,255,255,0.08)",
  tooltipBg: "#081226",
  tooltipBorder: "rgba(255,255,255,0.12)",
};

const PIE_COLORS = ["#fbbf24", "#38bdf8", "#34d399", "#a78bfa", "#fb7185"];

function FounderMetricsCharts({ metrics }) {
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <ChartCard title="Support trend (7 days)" hint="Opened vs resolved ticket flow over the last 7 days.">
        {metrics.supportTrend7d.length ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics.supportTrend7d}>
                <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                <XAxis dataKey="label" stroke={chartTheme.axis} tickLine={false} axisLine={false} />
                <YAxis stroke={chartTheme.axis} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: chartTheme.tooltipBg,
                    border: `1px solid ${chartTheme.tooltipBorder}`,
                    borderRadius: 12,
                    color: "white",
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="opened" name="Opened" stroke="#38bdf8" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="resolved" name="Resolved/Closed" stroke="#34d399" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState text="Not enough ticket history yet." />
        )}
      </ChartCard>

      <ChartCard title="Rating share" hint="Visual split of support ratings received.">
        {metrics.ratingPieData.some((x) => x.value > 0) ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={metrics.ratingPieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={3}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {metrics.ratingPieData.map((entry, index) => (
                    <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: chartTheme.tooltipBg,
                    border: `1px solid ${chartTheme.tooltipBorder}`,
                    borderRadius: 12,
                    color: "white",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState text="No ratings yet." />
        )}
      </ChartCard>

      <ChartCard title="Tickets by organization" hint="Which clients generate the most support load.">
        {metrics.ticketsByOrg.length ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.ticketsByOrg} layout="vertical" margin={{ left: 16, right: 8 }}>
                <CartesianGrid stroke={chartTheme.grid} horizontal={false} />
                <XAxis type="number" stroke={chartTheme.axis} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="label" width={120} stroke={chartTheme.axis} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  contentStyle={{
                    background: chartTheme.tooltipBg,
                    border: `1px solid ${chartTheme.tooltipBorder}`,
                    borderRadius: 12,
                    color: "white",
                  }}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} fill="#38bdf8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState text="No support tickets yet." />
        )}
      </ChartCard>

      <ChartCard title="Tickets by area" hint="Find the modules causing the most friction.">
        {metrics.ticketsByArea.length ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.ticketsByArea}>
                <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                <XAxis dataKey="label" stroke={chartTheme.axis} tickLine={false} axisLine={false} />
                <YAxis stroke={chartTheme.axis} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: chartTheme.tooltipBg,
                    border: `1px solid ${chartTheme.tooltipBorder}`,
                    borderRadius: 12,
                    color: "white",
                  }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#a78bfa" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState text="No support ticket areas yet." />
        )}
      </ChartCard>
    </div>
  );
}

export default function FounderMetricsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function run() {
      try {
        setLoading(true);
        setError("");

        const today = startOfToday();
        const month = startOfMonth();

        const safeDocs = async (...parts) => {
          try {
            const snap = await getDocs(collection(db, ...parts));
            return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          } catch {
            return [];
          }
        };

        const orgs = (await safeDocs("orgs")).filter(isActiveDoc);

        let activeStores = 0;
        let activeUsers = 0;
        let reportsToday = 0;
        let clockInsToday = 0;
        let reportsThisMonth = 0;
        let clockInsThisMonth = 0;
        let estimatedMRR = 0;
        let trialOrgs = 0;
        let paidOrgs = 0;

        const allSupportTickets = [];
        const allSupportMessages = [];

        for (const org of orgs) {
          const orgId = org.id;
          const billing = org.billing || {};
          const status = String(billing.status || org.status || "").toLowerCase();
          const monthlyFee = Number(billing.monthlyFee || 0);
          const pricePerStore = Number(billing.pricePerStore || 0);

          const storesNew = (await safeDocs("orgs", orgId, "stores")).filter(isActiveDoc);
          const storesLegacy = (await safeDocs("orgs", orgId, "cashup_stores")).filter(isActiveDoc);
          const storesCount = Math.max(storesNew.length, storesLegacy.length);
          activeStores += storesCount;

          const usersNew = (await safeDocs("orgs", orgId, "cashup_users")).filter(isActiveDoc);
          const usersLegacy = (await safeDocs("orgs", orgId, "cashup_staff")).filter(isActiveDoc);
          const managers = (await safeDocs("orgs", orgId, "cashup_managers")).filter(isActiveDoc);
          activeUsers += Math.max(usersNew.length, usersLegacy.length) + managers.length;

          if (status.includes("trial")) trialOrgs += 1;
          if (status === "active" || status === "paid" || status === "past_due") paidOrgs += 1;

          if (monthlyFee > 0) {
            estimatedMRR += monthlyFee;
          } else if (pricePerStore > 0 && storesCount > 0) {
            estimatedMRR += pricePerStore * storesCount;
          }

          const activeStoreIds = new Set([...storesNew, ...storesLegacy].map((x) => x.id));

          for (const storeId of Array.from(activeStoreIds)) {
            let shifts = (await safeDocs("orgs", orgId, "stores", storeId, "shifts")).filter(isActiveDoc);
            if (!shifts.length) {
              shifts = (await safeDocs("orgs", orgId, "cashup_stores", storeId, "shifts")).filter(isActiveDoc);
            }

            let submissions = (await safeDocs("orgs", orgId, "stores", storeId, "submissions")).filter(isActiveDoc);
            if (!submissions.length) {
              submissions = (await safeDocs("orgs", orgId, "cashup_stores", storeId, "submissions")).filter(isActiveDoc);
            }

            for (const row of shifts) {
              const dt = toJsDate(row.createdAt || row.clockInAt || row.date || row.startedAt);
              if (!dt) continue;
              if (dt >= today) clockInsToday += 1;
              if (dt >= month) clockInsThisMonth += 1;
            }

            for (const row of submissions) {
              const dt = toJsDate(row.submittedAt || row.createdAt || row.date);
              if (!dt) continue;
              if (dt >= today) reportsToday += 1;
              if (dt >= month) reportsThisMonth += 1;
            }
          }

          if (!activeStoreIds.size) {
            const legacyShifts = (await safeDocs("orgs", orgId, "cashup_shifts")).filter(isActiveDoc);
            const legacyForms = (await safeDocs("orgs", orgId, "cashup_forms")).filter(isActiveDoc);

            for (const row of legacyShifts) {
              const dt = toJsDate(row.createdAt || row.clockInAt || row.date || row.startedAt);
              if (!dt) continue;
              if (dt >= today) clockInsToday += 1;
              if (dt >= month) clockInsThisMonth += 1;
            }

            for (const row of legacyForms) {
              const dt = toJsDate(row.submittedAt || row.createdAt || row.date);
              if (!dt) continue;
              if (dt >= today) reportsToday += 1;
              if (dt >= month) reportsThisMonth += 1;
            }
          }

          const orgTickets = (await safeDocs("orgs", orgId, "supportTickets")).filter(isActiveDoc);

          for (const ticket of orgTickets) {
            const enrichedTicket = {
              ...ticket,
              orgId,
              orgName: ticket.orgName || org.name || org.orgName || org.businessName || org.title || orgId,
            };
            allSupportTickets.push(enrichedTicket);

            const messages = await safeDocs("orgs", orgId, "supportTickets", ticket.id, "messages");
            for (const message of messages) {
              allSupportMessages.push({
                ...message,
                orgId,
                ticketId: ticket.id,
              });
            }
          }
        }

        const demoLeads = await safeDocs("public_leads_book_demo");
        const foundingLeads = await safeDocs("public_leads_founding_partner");

        const leadsToday = [...demoLeads, ...foundingLeads].filter((x) => {
          const dt = toJsDate(x.createdAt);
          return dt && dt >= today;
        }).length;

        const leadFunnelTotal = demoLeads.length + foundingLeads.length;
        const trialToPaid = paidOrgs + trialOrgs > 0 ? (paidOrgs / (paidOrgs + trialOrgs)) * 100 : 0;

        const supportStatusCounts = {
          open: 0,
          inProgress: 0,
          waiting: 0,
          resolved: 0,
          closed: 0,
        };

        const ticketsByOrgMap = new Map();
        const ticketsByAreaMap = new Map();
        const ratingsMap = new Map([
          [1, 0],
          [2, 0],
          [3, 0],
          [4, 0],
          [5, 0],
        ]);

        const lowRatedTickets = [];
        const slowestTickets = [];
        const ratedValues = [];
        const resolutionHours = [];
        const firstReplyHours = [];

        const last7 = [];
        for (let i = 6; i >= 0; i -= 1) {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          d.setDate(d.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          last7.push({
            key,
            label: shortDate(d),
            opened: 0,
            resolved: 0,
          });
        }
        const trendMap = new Map(last7.map((x) => [x.key, x]));

        for (const ticket of allSupportTickets) {
          const statusKey = String(ticket.status || "open").toLowerCase();
          if (statusKey === "open") supportStatusCounts.open += 1;
          else if (statusKey === "in progress") supportStatusCounts.inProgress += 1;
          else if (statusKey === "waiting on client" || statusKey === "waiting") supportStatusCounts.waiting += 1;
          else if (statusKey === "resolved") supportStatusCounts.resolved += 1;
          else if (statusKey === "closed") supportStatusCounts.closed += 1;
          else supportStatusCounts.open += 1;

          const orgName = ticket.orgName || "Unknown org";
          ticketsByOrgMap.set(orgName, (ticketsByOrgMap.get(orgName) || 0) + 1);

          const area = String(ticket.area || ticket.module || "General").trim() || "General";
          ticketsByAreaMap.set(area, (ticketsByAreaMap.get(area) || 0) + 1);

          const createdAt = toJsDate(ticket.createdAt);
          const createdKey = createdAt ? createdAt.toISOString().slice(0, 10) : null;
          if (createdKey && trendMap.has(createdKey)) {
            trendMap.get(createdKey).opened += 1;
          }

          const stars = Number(ticket?.rating?.stars || 0);
          if (stars >= 1 && stars <= 5) {
            ratingsMap.set(stars, (ratingsMap.get(stars) || 0) + 1);
            ratedValues.push(stars);

            if (stars <= 3) {
              lowRatedTickets.push({
                id: ticket.id,
                reference: ticket.ticketNumber || ticket.reference || ticket.id,
                orgName,
                area,
                stars,
                comment: ticket?.rating?.comment || "",
                createdAt: ticket.createdAt,
              });
            }
          }

          const resolvedAt = toJsDate(ticket.resolvedAt || ticket.closedAt);
          const resolvedKey = resolvedAt ? resolvedAt.toISOString().slice(0, 10) : null;
          if (resolvedKey && trendMap.has(resolvedKey)) {
            trendMap.get(resolvedKey).resolved += 1;
          }

          if (createdAt && resolvedAt && resolvedAt >= createdAt) {
            const hrs = (resolvedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
            resolutionHours.push(hrs);
            slowestTickets.push({
              id: ticket.id,
              reference: ticket.ticketNumber || ticket.reference || ticket.id,
              orgName,
              area,
              hours: hrs,
              status: ticket.status || "—",
            });
          }

          const ticketMessages = allSupportMessages
            .filter((m) => m.ticketId === ticket.id && m.orgId === ticket.orgId)
            .sort((a, b) => {
              const ad = toJsDate(a.createdAt)?.getTime() || 0;
              const bd = toJsDate(b.createdAt)?.getTime() || 0;
              return ad - bd;
            });

          const firstSupportReply =
            ticketMessages.find((m) => {
              const senderType = String(m.senderType || m.authorType || "").toLowerCase();
              const senderName = String(m.senderName || "").toLowerCase();
              return senderType === "support" || senderName.includes("invoice mate");
            }) || null;

          const firstSupportReplyAt = toJsDate(ticket.firstReplyAt || firstSupportReply?.createdAt);
          if (createdAt && firstSupportReplyAt && firstSupportReplyAt >= createdAt) {
            const hrs = (firstSupportReplyAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
            firstReplyHours.push(hrs);
          }
        }

        slowestTickets.sort((a, b) => b.hours - a.hours);

        const avgRating = ratedValues.length > 0 ? ratedValues.reduce((sum, n) => sum + n, 0) / ratedValues.length : 0;
        const avgResolutionHours =
          resolutionHours.length > 0 ? resolutionHours.reduce((sum, n) => sum + n, 0) / resolutionHours.length : 0;
        const avgFirstReplyHours =
          firstReplyHours.length > 0 ? firstReplyHours.reduce((sum, n) => sum + n, 0) / firstReplyHours.length : 0;

        const ticketsByOrg = Array.from(ticketsByOrgMap.entries())
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);

        const ticketsByArea = Array.from(ticketsByAreaMap.entries())
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);

        const ratingDistribution = [5, 4, 3, 2, 1].map((stars) => ({
          stars,
          value: ratingsMap.get(stars) || 0,
        }));

        const ratingPieData = ratingDistribution
          .filter((x) => x.value > 0)
          .map((x) => ({ name: `${x.stars} Star`, value: x.value }));

        const next = {
          websiteVisitors: "GA4",
          websiteVisitorsHint: "Connect GA4 Data API in phase 2",
          demoRequests: demoLeads.length,
          foundingPartnerApplications: foundingLeads.length,
          leadsToday,
          activeOrganizations: orgs.length,
          activeStores,
          activeUsers,
          reportsToday,
          clockInsToday,
          reportsThisMonth,
          clockInsThisMonth,
          trialOrganizations: trialOrgs,
          paidOrganizations: paidOrgs,
          trialToPaid,
          estimatedMRR,
          leadFunnelTotal,

          totalTickets: allSupportTickets.length,
          supportStatusCounts,
          avgRating,
          avgResolutionHours,
          avgFirstReplyHours,
          ticketsByOrg,
          ticketsByArea,
          ratingDistribution,
          ratingPieData,
          supportTrend7d: Array.from(trendMap.values()),
          lowRatedTickets: lowRatedTickets
            .sort((a, b) => a.stars - b.stars || (toJsDate(b.createdAt)?.getTime() || 0) - (toJsDate(a.createdAt)?.getTime() || 0))
            .slice(0, 6),
          slowestTickets: slowestTickets.slice(0, 6),
        };

        if (mounted) setMetrics(next);
      } catch (err) {
        console.error(err);
        if (mounted) setError(err?.message || "Failed to load founder metrics.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    run();
    return () => {
      mounted = false;
    };
  }, []);

  const cards = useMemo(() => {
    if (!metrics) return [];
    return [
      { label: "Website visitors", value: metrics.websiteVisitors, sub: metrics.websiteVisitorsHint, accent: "text-amber-300" },
      { label: "Demo requests", value: metrics.demoRequests, sub: `Founding partner: ${metrics.foundingPartnerApplications}`, accent: "text-cyan-300" },
      { label: "Leads today", value: metrics.leadsToday, sub: `Total lead records: ${metrics.leadFunnelTotal}`, accent: "text-emerald-300" },
      { label: "Active organizations", value: metrics.activeOrganizations, sub: `Paid: ${metrics.paidOrganizations} · Trial: ${metrics.trialOrganizations}` },
      { label: "Active stores", value: metrics.activeStores, sub: "Across all organizations" },
      { label: "Active users", value: metrics.activeUsers, sub: "Managers + staff" },
      { label: "Reports today", value: metrics.reportsToday, sub: `This month: ${metrics.reportsThisMonth}` },
      { label: "Clock-ins today", value: metrics.clockInsToday, sub: `This month: ${metrics.clockInsThisMonth}` },
      { label: "Trial → paid", value: pct(metrics.trialToPaid), sub: "Based on current org billing status", accent: "text-fuchsia-300" },
      { label: "Estimated MRR", value: money(metrics.estimatedMRR), sub: "From billing.monthlyFee or pricePerStore", accent: "text-emerald-300" },
    ];
  }, [metrics]);

  const supportCards = useMemo(() => {
    if (!metrics) return [];
    return [
      {
        label: "Total tickets",
        value: metrics.totalTickets,
        sub: `Open: ${metrics.supportStatusCounts.open} · Closed: ${metrics.supportStatusCounts.closed}`,
      },
      {
        label: "Avg rating",
        value: metrics.avgRating > 0 ? `${metrics.avgRating.toFixed(1)} ★` : "—",
        sub: "From resolved/closed client ratings",
        accent: "text-amber-300",
        tone: "bg-amber-500/10",
      },
      {
        label: "Avg first reply",
        value: formatHours(metrics.avgFirstReplyHours),
        sub: "Time from ticket created to first support reply",
        accent: "text-cyan-300",
        tone: "bg-cyan-500/10",
      },
      {
        label: "Avg resolution time",
        value: formatHours(metrics.avgResolutionHours),
        sub: "Time from ticket created to resolved/closed",
        accent: "text-emerald-300",
        tone: "bg-emerald-500/10",
      },
      {
        label: "Resolved tickets",
        value: metrics.supportStatusCounts.resolved + metrics.supportStatusCounts.closed,
        sub: `Waiting: ${metrics.supportStatusCounts.waiting} · In progress: ${metrics.supportStatusCounts.inProgress}`,
        accent: "text-violet-300",
        tone: "bg-violet-500/10",
      },
    ];
  }, [metrics]);

  if (loading) {
    return <div className="text-white/70">Loading founder metrics…</div>;
  }

  if (error) {
    return <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-200">{error}</div>;
  }

  return (
    <section className="space-y-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-300">Founder Dashboard</div>
          <h1 className="mt-2 text-3xl font-bold">Super Admin founder metrics</h1>
          <p className="mt-2 max-w-3xl text-white/60">
            This dashboard lives inside Super Admin and pulls live operational metrics from Firestore. Website visitor totals still need the GA4 Data API,
            so this version shows GA4 as a placeholder while operations, support, ratings, and ticket metrics are live.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/super/orgs" className="rounded-full border border-white/10 px-4 py-2 text-sm hover:bg-white/5">
            View organizations
          </Link>
          <a
            href="https://analytics.google.com/analytics/web/"
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-cyan-600 px-4 py-2 text-sm hover:bg-cyan-500"
          >
            Open GA4
          </a>
        </div>
      </div>

      <div>
        <SectionTitle title="Snapshot" hint="Operational and lead metrics that matter most for Invoice Mate growth." />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {cards.map((card) => (
            <StatCard key={card.label} {...card} />
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <SectionTitle title="Lead funnel" hint="Website forms already write to Firestore, so these are live right now." />
          <div className="space-y-3 text-sm">
            <Row label="Demo requests" value={metrics.demoRequests} />
            <Row label="Founding partner applications" value={metrics.foundingPartnerApplications} />
            <Row label="Combined leads" value={metrics.leadFunnelTotal} />
            <Row label="Leads submitted today" value={metrics.leadsToday} />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <SectionTitle title="Revenue lens" hint="This uses org billing fields already stored in Firestore." />
          <div className="space-y-3 text-sm">
            <Row label="Paid organizations" value={metrics.paidOrganizations} />
            <Row label="Trial organizations" value={metrics.trialOrganizations} />
            <Row label="Trial → paid conversion" value={pct(metrics.trialToPaid)} />
            <Row label="Estimated MRR" value={money(metrics.estimatedMRR)} />
          </div>
        </div>
      </div>

      <div>
        <SectionTitle
          title="Support performance"
          hint="This turns your ticketing system into founder-level support intelligence across all organizations."
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {supportCards.map((card) => (
            <StatCard key={card.label} {...card} />
          ))}
        </div>
      </div>

      <FounderMetricsCharts metrics={metrics} />

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <SectionTitle title="Ticket status mix" hint="Current support workload by lifecycle stage." />
          <div className="grid gap-3 sm:grid-cols-2">
            <MiniMetric label="Open" value={metrics.supportStatusCounts.open} tone="bg-blue-500/10 text-blue-200" />
            <MiniMetric label="In Progress" value={metrics.supportStatusCounts.inProgress} tone="bg-cyan-500/10 text-cyan-200" />
            <MiniMetric label="Waiting" value={metrics.supportStatusCounts.waiting} tone="bg-amber-500/10 text-amber-200" />
            <MiniMetric label="Resolved" value={metrics.supportStatusCounts.resolved} tone="bg-emerald-500/10 text-emerald-200" />
            <MiniMetric label="Closed" value={metrics.supportStatusCounts.closed} tone="bg-slate-500/10 text-slate-200" />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <SectionTitle title="Rating distribution" hint="How clients are scoring support quality." />
          <div className="space-y-3">
            {metrics.ratingDistribution.map((row) => (
              <div key={row.stars}>
                <div className="mb-1 flex items-center justify-between gap-4 text-sm">
                  <div className="text-white/70">{row.stars} Star</div>
                  <div className="font-semibold text-white">{row.value}</div>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-white/5">
                  <div
                    className="h-full rounded-full bg-amber-400/80"
                    style={{ width: `${Math.max(8, (Number(row.value || 0) / Math.max(...metrics.ratingDistribution.map((x) => x.value), 1)) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <SectionTitle title="Low rated tickets" hint="These are the most important tickets to learn from." />
          <div className="space-y-3">
            {metrics.lowRatedTickets.length ? (
              metrics.lowRatedTickets.map((ticket) => (
                <div key={ticket.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-white">{ticket.reference}</div>
                      <div className="mt-1 text-sm text-white/60">
                        {ticket.orgName} · {ticket.area}
                      </div>
                    </div>
                    <Stars value={ticket.stars} />
                  </div>
                  {ticket.comment ? <div className="mt-3 text-sm text-white/75">{ticket.comment}</div> : null}
                  <div className="mt-3 text-xs text-white/40">{formatDateTime(ticket.createdAt)}</div>
                </div>
              ))
            ) : (
              <EmptyState text="No low-rated tickets yet." />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <SectionTitle title="Slowest tickets" hint="Tickets with the longest resolution times." />
          <div className="space-y-3">
            {metrics.slowestTickets.length ? (
              metrics.slowestTickets.map((ticket) => (
                <div key={ticket.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-white">{ticket.reference}</div>
                      <div className="mt-1 text-sm text-white/60">
                        {ticket.orgName} · {ticket.area}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-amber-300">{formatHours(ticket.hours)}</div>
                      <div className="mt-1">
                        <StatusPill status={ticket.status} />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState text="Not enough resolved tickets yet." />
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <SectionTitle title="What to add next" hint="The page is live now. These are the next upgrades that will make it even stronger." />
        <ul className="space-y-2 text-sm text-white/70">
          <li>• Add 7-day and 30-day trend comparisons for ticket volume, ratings, and response times.</li>
          <li>• Add support notification analytics so founder metrics also shows unread replies and active support load.</li>
          <li>• Add issue hotspot intelligence so the system highlights the most problematic module automatically.</li>
        </ul>
      </div>
    </section>
  );
}
